import React, { useState, useMemo, memo } from "react";
import Chart from "react-apexcharts";
import { FiBarChart2, FiAlertOctagon, FiAward, FiLayers } from "react-icons/fi";
import "./RcrLeaderboardReport.css";

// Safe score parser helper (handles "85%", 85, null, undefined, NaN)
const parseScore = (val) => {
  if (val === null || val === undefined) return 0;
  const num = parseFloat(String(val).replace("%", "").trim());
  return isNaN(num) ? 0 : Math.round(num * 10) / 10;
};

// Truncate long text helper (allows 22 chars so branch names like "Fine Pizza Ameer..." show clearly)
const truncateText = (val, maxLen = 20) => {
  if (val === null || val === undefined) return "";
  const str = String(val);
  return str.length > maxLen ? `${str.slice(0, maxLen).trim()}...` : str;
};

// 1. Memoized Audit Outcome Donut Component
const AuditOutcomeDonut = memo(({ branchesPassed, branchesFailed, branchesAudited }) => {
  const total = branchesPassed + branchesFailed;
  const passRate = total > 0 ? `${Math.round((branchesPassed / total) * 100)}%` : "0%";
  const donutSeries = useMemo(() => [branchesPassed, branchesFailed], [branchesPassed, branchesFailed]);

  const donutOptions = useMemo(() => ({
    chart: {
      type: "donut",
      animations: { enabled: true, easing: "easeinout", speed: 500 },
      toolbar: { show: false },
    },
    labels: ["Passed", "Failed"],
    colors: ["#16a34a", "#dc2626"],
    dataLabels: {
      enabled: false,
    },
    stroke: {
      width: 2,
      colors: ["#ffffff"],
    },
    legend: {
      position: "bottom",
      horizontalAlign: "center",
      fontSize: "13px",
      fontWeight: 600,
      markers: { radius: 12, width: 10, height: 10 },
      itemMargin: { horizontal: 12, vertical: 4 },
    },
    tooltip: {
      custom: ({ series, seriesIndex }) => {
        const sum = series && series.length ? series.reduce((acc, val) => acc + (Number(val) || 0), 0) : 0;
        const count = Number(series?.[seriesIndex]) || 0;
        const pct = sum > 0 ? ((count / sum) * 100).toFixed(1) : "0";
        const label = seriesIndex === 0 ? "Passed" : "Failed";
        const bg = seriesIndex === 0 ? "#16a34a" : "#dc2626";

        return `
          <div style="padding: 8px 12px; font-size: 13px; font-weight: 600; color: #fff; background: ${bg}; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.15);">
            <div>${label}: ${count} ${count === 1 ? "branch" : "branches"}</div>
            <div style="font-size: 11px; opacity: 0.9; margin-top: 2px;">${pct}% of audited branches</div>
          </div>
        `;
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: "72%",
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: "13px",
              fontWeight: 600,
              color: "#64748b",
              offsetY: -4,
            },
            value: {
              show: true,
              fontSize: "22px",
              fontWeight: 700,
              color: "#0f172a",
              offsetY: 4,
              formatter: () => passRate,
            },
            total: {
              show: true,
              label: "Pass Rate",
              fontWeight: 600,
              fontSize: "12px",
              color: "#64748b",
              formatter: () => passRate,
            },
          },
        },
      },
    },
  }), [passRate]);

  return (
    <div className="col-lg-4 col-md-12 mb-3 px-2">
      <div className="card h-100 rcr-chart-card">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h5 className="rcr-chart-title mt-1">
              Audit Outcome
            </h5>
            <p className="rcr-chart-subtitle">
              Pass vs Fail across {branchesAudited} branches
            </p>
          </div>
          <span 
            className={`badge rcr-chart-badge ${
              branchesPassed > 0 ? "rcr-chart-badge-pass" : "rcr-chart-badge-fail"
            }`}
          >
            {passRate} Pass Rate
          </span>
        </div>

        <div className="d-flex flex-column justify-content-center align-items-center flex-grow-1 rcr-chart-canvas-donut">
          {total === 0 ? (
            <div className="rcr-chart-empty py-4">
              No branch audit status recorded.
            </div>
          ) : (
            <Chart
              key={`donut-${branchesPassed}-${branchesFailed}`}
              options={donutOptions}
              series={donutSeries}
              type="donut"
              width="100%"
              height="230px"
            />
          )}
        </div>

        <div className="rcr-donut-footer">
          <div className="text-center">
            <span className="rcr-donut-dot rcr-donut-dot-pass"></span>
            <span className="rcr-donut-stat-label">Passed:</span>
            <span className="rcr-donut-stat-val text-success">{branchesPassed}</span>
          </div>
          <div className="text-center">
            <span className="rcr-donut-dot rcr-donut-dot-fail"></span>
            <span className="rcr-donut-stat-label">Failed:</span>
            <span className="rcr-donut-stat-val text-danger">{branchesFailed}</span>
          </div>
        </div>
      </div>
    </div>
  );
});

// 2. Memoized Branch Benchmark & Risk Bar Component
const BranchBenchmarkBar = memo(({ branches, categories }) => {
  const [metricView, setMetricView] = useState("score"); // "score" | "ncs" | "categories"
  const [barOrientation, setBarOrientation] = useState("hbar"); // "hbar" | "bar"

  // Safely fallback if categories are requested but none exist
  const activeMetric = (metricView === "categories" && categories.length === 0) ? "score" : metricView;
  const isHorizontal = barOrientation === "hbar";

  const chartData = useMemo(() => {
    if (activeMetric === "categories") {
      const labels = categories.map((c) => c.category || c.name || "Category");
      const values = categories.map((c) => parseScore(c.portfolio_avg_score_raw ?? c.portfolio_avg_score ?? c.avg_score));
      const colors = values.map((val) => {
        if (val >= 60) return "#16a34a";
        if (val >= 50) return "#d97706";
        return "#dc2626";
      });

      return {
        categories: labels,
        series: [{ name: "Category Avg Score (%)", data: values }],
        colors: colors.length > 0 ? colors : ["#16a34a"],
        unit: "%",
      };
    }

    if (activeMetric === "ncs") {
      const labels = branches.map((b) => b.branch_name || b.name || "Branch");
      const values = branches.map((b) => Math.max(0, parseInt(b.critical_ncs || 0, 10) || 0));
      const colors = values.map(() => "#dc2626");

      return {
        categories: labels,
        series: [{ name: "Critical NCs", data: values }],
        colors: colors.length > 0 ? colors : ["#dc2626"],
        unit: "",
      };
    }

    // Default: Overall Score
    const labels = branches.map((b) => b.branch_name || b.name || "Branch");
    const values = branches.map((b) => parseScore(b.overall_score_raw ?? b.overall_score));
    const colors = values.map((val) => {
      if (val >= 60) return "#16a34a";
      if (val >= 50) return "#d97706";
      return "#dc2626";
    });

    return {
      categories: labels,
      series: [{ name: "Overall Score (%)", data: values }],
      colors: colors.length > 0 ? colors : ["#16a34a"],
      unit: "%",
    };
  }, [branches, categories, activeMetric]);

  const barOptions = useMemo(() => ({
    chart: {
      type: "bar",
      animations: { enabled: true, easing: "easeinout", speed: 400 },
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: isHorizontal,
        borderRadius: 6,
        distributed: true,
        barHeight: "55%",
        columnWidth: "45%",
        dataLabels: {
          position: "top",
        },
      },
    },
    colors: chartData.colors,
    legend: { show: false },
    dataLabels: {
      enabled: true,
      textAnchor: isHorizontal ? "start" : "middle",
      offsetX: isHorizontal ? 6 : 0,
      offsetY: isHorizontal ? 0 : -18,
      style: {
        fontSize: "11px",
        fontWeight: 700,
        colors: ["#334155"],
      },
      formatter: (val) => `${val}${chartData.unit}`,
    },
    xaxis: {
      categories: chartData.categories,
      min: isHorizontal && activeMetric !== "ncs" ? 0 : undefined,
      max: isHorizontal && activeMetric !== "ncs" ? 100 : undefined,
      tickAmount: isHorizontal && activeMetric !== "ncs" ? 10 : undefined,
      labels: {
        rotate: isHorizontal ? 0 : -35,
        rotateAlways: !isHorizontal,
        trim: false,
        maxHeight: isHorizontal ? undefined : 80,
        style: {
          fontSize: "11px",
          fontWeight: 600,
          colors: "#64748b",
        },
        formatter: (val) => {
          if (isHorizontal) {
            return chartData.unit ? `${val}${chartData.unit}` : val;
          }
          return truncateText(val, 18);
        },
      },
    },
    yaxis: {
      min: !isHorizontal && activeMetric !== "ncs" ? 0 : undefined,
      max: !isHorizontal && activeMetric !== "ncs" ? 100 : undefined,
      tickAmount: !isHorizontal && activeMetric !== "ncs" ? 5 : undefined,
      labels: {
        style: {
          fontSize: "11px",
          fontWeight: 600,
          colors: "#334155",
        },
        formatter: (val) => {
          if (!isHorizontal) {
            return chartData.unit ? `${val}${chartData.unit}` : val;
          }
          return truncateText(val, 20);
        },
      },
    },
    grid: {
      borderColor: "#f1f5f9",
      strokeDashArray: 3,
    },
    tooltip: {
      theme: "light",
      x: {
        show: true,
        formatter: (val, opts) => {
          const idx = opts?.dataPointIndex;
          if (idx !== undefined && chartData.categories[idx]) {
            return chartData.categories[idx];
          }
          return val;
        },
      },
      y: {
        formatter: (val) => `${val}${chartData.unit}`,
      },
    },
  }), [chartData, isHorizontal, activeMetric]);

  return (
    <div className="col-lg-8 col-md-12 mb-3 px-2">
      <div className="card h-100 rcr-chart-card">
        {/* Header & Controls */}
        <div className="rcr-chart-header-row">
          <div className="rcr-chart-header-title-col">
            <h5 className="rcr-chart-title text-truncate mt-1">
              {activeMetric === "score" && "Branch Compliance Scores"}
              {activeMetric === "ncs" && "Critical NCs by Branch"}
              {activeMetric === "categories" && "Portfolio Average by Category"}
            </h5>
            <p className="rcr-chart-subtitle text-truncate">
              {activeMetric === "score" && "Green: ≥60% | Amber: 50-59% | Red: <50%"}
              {activeMetric === "ncs" && "Total critical NCs per branch"}
              {activeMetric === "categories" && "Audit section performance across portfolio"}
            </p>
          </div>

          <div className="rcr-chart-controls">
            {/* Metric Selector Buttons */}
            <div className="rcr-chart-pill-group">
              <button
                type="button"
                className={`rcr-chart-pill-btn ${activeMetric === "score" ? "active-score" : ""}`}
                onClick={() => setMetricView("score")}
              >
                <FiAward className="mr-1" /> Score (%)
              </button>
              <button
                type="button"
                className={`rcr-chart-pill-btn ${activeMetric === "ncs" ? "active-ncs" : ""}`}
                onClick={() => setMetricView("ncs")}
              >
                <FiAlertOctagon className="mr-1" /> Critical NCs
              </button>
              {categories.length > 0 && (
                <button
                  type="button"
                  className={`rcr-chart-pill-btn ${activeMetric === "categories" ? "active-categories" : ""}`}
                  onClick={() => setMetricView("categories")}
                >
                  <FiLayers className="mr-1" /> Categories
                </button>
              )}
            </div>

            {/* Bar Orientation Toggle */}
            <div className="rcr-chart-orient-group">
              <div 
                className={`rcr-chart-orient-btn ${barOrientation === "hbar" ? "active" : ""}`}
                onClick={() => setBarOrientation("hbar")}
                title="Horizontal Bar Chart"
              >
                <FiBarChart2 size={13} className="rcr-rotate-90" />
              </div>
              <div 
                className={`rcr-chart-orient-btn ${barOrientation === "bar" ? "active" : ""}`}
                onClick={() => setBarOrientation("bar")}
                title="Vertical Column Chart"
              >
                <FiBarChart2 size={13} />
              </div>
            </div>
          </div>
        </div>

        {/* Chart Canvas */}
        <div className="rcr-chart-canvas-bar">
          {chartData.categories.length === 0 ? (
            <div className="rcr-chart-empty py-5">
              No branch performance data available.
            </div>
          ) : (
            <Chart
              key={`bar-${activeMetric}-${barOrientation}`}
              options={barOptions}
              series={chartData.series}
              type="bar"
              width="100%"
              height="260px"
            />
          )}
        </div>
      </div>
    </div>
  );
});

// Main Container Component wrapped in memo
const RcrComplianceCharts = ({ data, sortedBranches }) => {
  const snapshot = data?.portfolio_snapshot || {};
  const branchesAudited = Math.max(0, parseInt(snapshot.branches_audited || 0, 10) || 0);
  const branchesPassed = Math.max(0, parseInt(snapshot.branches_passed || 0, 10) || 0);
  const branchesFailed = Math.max(0, parseInt(snapshot.branches_failed || 0, 10) || 0);

  const branchesData = data?.branches_ranked_by_risk;
  const branches = useMemo(() => {
    if (sortedBranches && sortedBranches.length > 0) return sortedBranches;
    return branchesData || [];
  }, [sortedBranches, branchesData]);

  const categoryRisk = data?.category_level_risk;
  const categoryHeatmap = data?.category_heatmap;
  const categories = useMemo(() => {
    const list = categoryRisk?.vcategory || 
                 categoryRisk?.categories || 
                 [];
    if (list.length > 0) return list;
    
    // Fallback: extract from category_heatmap columns and calculate average from rows
    if (categoryHeatmap?.columns && categoryHeatmap?.rows) {
      return categoryHeatmap.columns.map((col, idx) => {
        let total = 0;
        let count = 0;
        categoryHeatmap.rows.forEach((row) => {
          if (row.scores && row.scores[idx]) {
            const val = parseScore(row.scores[idx].score);
            if (!isNaN(val)) {
              total += val;
              count++;
            }
          }
        });
        const avg = count > 0 ? (total / count).toFixed(1) : 0;
        return {
          category: col.category,
          weightage: col.weightage,
          portfolio_avg_score: `${avg}%`,
          portfolio_avg_score_raw: parseFloat(avg)
        };
      });
    }
    return [];
  }, [categoryRisk, categoryHeatmap]);

  if (!data?.portfolio_snapshot && branches.length === 0) {
    return null;
  }

  return (
    <div className="row mb-4 mx-0">
      <AuditOutcomeDonut 
        branchesPassed={branchesPassed} 
        branchesFailed={branchesFailed} 
        branchesAudited={branchesAudited} 
      />
      <BranchBenchmarkBar 
        branches={branches} 
        categories={categories} 
      />
    </div>
  );
};

export default memo(RcrComplianceCharts);
